import React, { Fragment } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import RInputMask from 'react-input-mask';
import {
  color,
  fontSize,
  fontWeight,
  width,
  opacity,
  height,
  border,
  padding,
  margin,
  position,
} from 'styled-system';
import Wrapper from '../Wrapper';
import TextInput from './TextInput';

const BaseTextInputMask = styled(RInputMask)`
  ${width};
  ${position};
  ${border};
  ${color};
  ${opacity};
  ${height};
  ${fontSize};
  ${fontWeight};
  ${padding};
  ${margin};
`;

const inputDefaultProps = {
  width: 1,
  border: 'none',
  fontSize: 16,
  color: 'transparent',
  fontWeight: 400,
  margin: 0,
  padding: 0,
};

const InputMaskStyled = styled(BaseTextInputMask)`
  outline: none;
  position: absolute;
  background: transparent;
  line-height: 20px;
  margin-top: -20px;
  caret-color: ${({ theme }) => theme.colors.darkBlack};
  &::placeholder {
    color: ${({ theme }) => theme.colors.greyDark};
    opacity: 0.5;
  }
`;

InputMaskStyled.defaultProps = inputDefaultProps;

const BoxValueStyled = props => <Wrapper {...props} />;

BoxValueStyled.defaultProps = {
  ...inputDefaultProps,
  color: 'darkBlack',
  height: 20,
  position: 'relative',
  opacity: 1,
};

const ContentStyled = styled.span`
  ${color};
  ${opacity};
`;

const parseMask = ({ value, maskPlaceholder, mask }) => {
  const maskPlaceholderChars = maskPlaceholder.split('');
  const maskChars =
    typeof mask === 'string'
      ? mask.replace(/[A-Za-z0-9]/g, '').split('')
      : mask;
  const elements = [];
  let diff = true;
  let chars = '';
  value.split('').forEach((valueChar, index) => {
    if (
      (diff && valueChar !== maskPlaceholderChars[index]) ||
      (!diff &&
        valueChar === maskPlaceholderChars[index] &&
        !maskChars.some(maskChar =>
          typeof maskChars[index] === 'string'
            ? valueChar === maskChar
            : new RegExp(maskChar, 'g').test(valueChar)
        ))
    ) {
      elements.push({ content: chars, diff });
      chars = '';
      diff = !diff;
    }
    chars = chars + valueChar;
  });
  if (chars) {
    elements.push({ content: chars, diff });
  }
  return elements.filter(({ content }) => content);
};

const updateNextState = ({
  value,
  maskPlaceholder,
  maskChar,
  renderBlock,
  startIndex,
}) => {
  const blockValues = value.split(maskChar);
  let selection = {
    start: startIndex - 1,
    end: startIndex - 1,
  };
  let selectionStart = blockValues[0].length + 1;
  let blockIndex = -1;
  const blockValue = blockValues.find((block, index) => {
    if (selectionStart >= startIndex) {
      blockIndex = index;
      return block;
    }
    selectionStart = selectionStart + block.length + 1;
    return false;
  });

  const blockPlaceHolders = maskPlaceholder.split(maskChar);
  if (blockValue && blockValue !== blockPlaceHolders[blockIndex]) {
    blockValues[blockIndex] = renderBlock(blockValue);
    selection = {
      start: selectionStart,
      end: selectionStart,
    };
  }
  return {
    value: blockValues.join(maskChar),
    selection,
  };
};

const createMaskPlaceholder = ({
  value,
  mask,
  maskPlaceholder,
  showMaskPlaceholder,
}) => {
  if (mask && value && maskPlaceholder && showMaskPlaceholder) {
    const elements = parseMask({ value, mask, maskPlaceholder });
    return elements.map(({ content, diff }, index) =>
      diff ? (
        <ContentStyled key={index} opacity={0.3} color="greyDark">
          {content}
        </ContentStyled>
      ) : (
        <ContentStyled key={index}>{content}</ContentStyled>
      )
    );
  }
  return value;
};

export const InputMask = ({
  focused,
  value,
  mask,
  maskPlaceholder,
  showMaskPlaceholder = true,
  maskChar,
  placeholder,
  onChange,
  renderBlock,
  ...props
}) => {
  const handleChange = event => {
    const { value: targetValue } = event.target;
    const emptyValue = targetValue === maskPlaceholder;
    if (!value && emptyValue) {
      return;
    }
    event.target.value = emptyValue ? '' : targetValue;
    onChange(event);
  };

  function beforeMaskedStateChange({ previousState = {}, nextState }) {
    const { value: newValue, selection } = nextState;
    const { value: previousValue } = previousState;
    let newState = {};
    if (
      renderBlock &&
      maskChar &&
      maskPlaceholder &&
      nextState.enteredString === maskChar &&
      newValue !== previousValue
    ) {
      const { start: startIndex } = selection;
      newState = updateNextState({
        value: previousState.value,
        maskPlaceholder,
        maskChar,
        renderBlock,
        startIndex,
      });
    }
    return {
      ...nextState,
      value: newValue,
      ...newState,
    };
  }

  return (
    <Fragment>
      <BoxValueStyled>
        {showMaskPlaceholder &&
          (value
            ? createMaskPlaceholder({
                value,
                mask,
                maskPlaceholder,
                showMaskPlaceholder,
              })
            : focused && (
                <ContentStyled opacity={0.3} color="greyDark">
                  {maskPlaceholder}
                </ContentStyled>
              ))}
      </BoxValueStyled>
      <InputMaskStyled
        {...props}
        value={value}
        onChange={handleChange}
        mask={mask}
        maskPlaceholder={maskPlaceholder}
        placeholder={placeholder}
        beforeMaskedStateChange={beforeMaskedStateChange}
      />
    </Fragment>
  );
};

InputMask.propTypes = {
  focused: PropTypes.bool,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  maskPlaceholder: PropTypes.string,
  maskChar: PropTypes.string,
  showMaskPlaceholder: PropTypes.bool,
  onChange: PropTypes.func,
  renderBlock: PropTypes.func,
  mask: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export const TextInputMask = props => (
  <TextInput {...props}>
    <InputMask />
  </TextInput>
);

export default TextInputMask;
